import { useEffect, useState } from "react";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import { acqService, type AcqStrategyView } from "../../api/acqService";

/**
 * 本周押什么(契约 §12.4 `GET /acq/strategy`)。
 *
 * ★★ 这张卡回答的是**「这周先做哪条线」**,不是「这周数据长什么样」。所以:
 *   · 只写环比,**不写「多采 ×1.4」这类操作建议** —— 我们没有证据支撑那个系数,
 *     写出来就是把一个编的数伪装成结论。
 *   · 涨跌都给。只给涨的等于永远押同一条线,而真正有用的是"商标注册 −8%,这周别硬推"。
 * ★ `available=false` = 该行业还没铸 007 信号词典 ⇒ 没有种子词、算不出趋势。
 *   这不是故障,如实说;**不许拿一份编的趋势充数**。
 */
export function StrategyCard() {
  const [view, setView] = useState<AcqStrategyView | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    acqService.strategy().then(
      (v) => alive && setView(v),
      () => alive && setView(null)
    );
    return () => {
      alive = false;
    };
  }, []);

  if (view === undefined) return <Skeleton className="h-48 w-full rounded-xl" />;

  if (!view || !view.available) {
    return (
      <section className="rounded-xl border border-dashed bg-card px-4 py-3">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
          <TrendingUp className="h-4 w-4" />
          本周趋势暂无
        </p>
        <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
          这个行业还没铸信号词典，算不出哪个词在涨。
          <span className="font-semibold text-foreground">不是没数据就编一个给你看。</span>
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border bg-card px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
        <h3 className="flex items-center gap-1.5 text-sm font-bold">
          <TrendingUp className="h-4 w-4 text-primary" />
          本周押什么
        </h3>
        {view.weekStart && (
          <span className="text-2xs text-muted-foreground">
            {view.weekStart.slice(5)} ~ {view.weekEnd?.slice(5)}
          </span>
        )}
      </div>

      <ul className="mt-2.5 space-y-1.5">
        {view.rows.map((r) => (
          <li key={r.keyword} className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 truncate">{r.keyword}</span>
            <span
              className={cn(
                "shrink-0 rounded-full px-1.5 py-px text-2xs font-bold tabular-nums",
                r.deltaPct > 0
                  ? "bg-success/10 text-success"
                  : r.deltaPct < 0
                    ? "bg-destructive/10 text-destructive"
                    : "bg-foreground/5 text-muted-foreground"
              )}
              title={`近 7 天 ${r.last7} / 30 天均 ${r.avg30}`}
            >
              {r.deltaPct > 0 ? "+" : ""}
              {r.deltaPct}%
            </span>
          </li>
        ))}
      </ul>

      {view.newSceneWords.length > 0 && (
        <div className="mt-3 border-t pt-2.5">
          <p className="text-2xs font-semibold text-muted-foreground">新冒出来的说法</p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {view.newSceneWords.map((w) => (
              <span key={w} className="rounded-full border bg-background px-2 py-px text-2xs">
                {w}
              </span>
            ))}
          </div>
        </div>
      )}

      <Button asChild variant="outline" size="sm" className="mt-3 w-full">
        <Link to="/ai-explosive">
          去追爆仓看内容
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </Button>
    </section>
  );
}
